import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { flipApiRequests } from 'utils/api-requests/flip';
import { CalculatorInputType, CalculatorSettings, initialCalculatorState } from 'utils/types/Calculator';

import CalculatorTable, { CalculatorSection } from 'components/calculator/CalculatorTable';
import V2HeaderSection from 'components/V2HeaderSection';

const Wrapper = styled.div`
    margin-bottom: 20px;
    width: 100%;
    paddingleft: 20px;
`;

const Container = styled.div`
    margin: 15px 20px 0;
    padding: 10px 120px;
    display: flex;

    @media (max-width: 1100px) {
        padding: 20px 20px 0;
    }
`;

const CalculatorSettingsPage = () => {
    const [calculatorState, setCalculatorState] = useState<CalculatorSettings>(initialCalculatorState);

    useEffect(() => {
        const fetchFlipDefaultValues = async () => {
            const flipDefaultValues = await flipApiRequests.getFlipDefaultValues();

            if (Object.keys(flipDefaultValues).length > 0) {
                setCalculatorState(flipDefaultValues as CalculatorSettings);
            }
        };

        fetchFlipDefaultValues();
    }, []);

    const handleUpdateField = async () => {
        const response = await flipApiRequests.updateDefaultFlipValues(calculatorState);

        if (Object.keys(response).length > 0) {
            setCalculatorState(response as CalculatorSettings);
        }
    };

    const handleChange = (fieldName: keyof CalculatorSettings, value: string) => {
        setCalculatorState((prev) => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleReset = async () => {
        const nullValues: Record<keyof CalculatorSettings, null> = Object.keys(calculatorState).reduce((acc, key) => {
            acc[key as keyof CalculatorSettings] = null;

            return acc;
        }, {} as Record<keyof CalculatorSettings, null>);

        // @ts-expect-error trzeba wysłać null, aby Api zwróciło wartości domyślne
        const response = await flipApiRequests.resetDefaultFlipValues(nullValues);

        if (Object.keys(response).length > 0) {
            setCalculatorState(response as CalculatorSettings);
        }
    };

    const handleResetField = async (fieldName: keyof CalculatorSettings) => {
        const requestData = { ...calculatorState, [fieldName]: null };

        const response = await flipApiRequests.updateDefaultFlipValues(requestData);

        if (Object.keys(response).length > 0) {
            setCalculatorState(response as CalculatorSettings);
        }
    };

    const calculatorStructure: CalculatorSection[] = [
        {
            title: 'Podstawowe parametry',
            fields: [
                {
                    title: 'Maksymalny czas trwania flipa',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'flipTime',
                    value: calculatorState?.flipTime || '',
                    min: 1,
                    max: 36,
                    suffix: 'miesięcy'
                }
            ]
        },
        {
            title: 'Zysk',
            fields: [
                {
                    title: 'Minimalna kwota zysku',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'minProfitAmount',
                    value: calculatorState?.minProfitAmount || '',
                    min: 1,
                    suffix: 'PLN'
                },
                {
                    title: 'Minimalny procent zysku',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'minProfitPercent',
                    value: calculatorState.minProfitPercent,
                    min: 0,
                    max: 100,
                    suffix: '%'
                }
            ]
        },
        {
            title: 'Koszty',
            fields: [
                {
                    title: 'Notariusz',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'notary',
                    value: calculatorState.notary,
                    min: 0,
                    max: 30000,
                    suffix: 'PLN'
                },
                {
                    title: 'Pośrednik przy zakupie mieszkania',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'brokerPercent',
                    value: calculatorState.brokerPercent,
                    min: 0,
                    max: 100,
                    suffix: '%'
                },
                {
                    title: 'Opłaty eksploatacyjne miesięczne za m2',
                    helperText: 'czynsz',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'rentFees',
                    value: calculatorState.rentFees,
                    min: 0,
                    max: 10000,
                    suffix: 'PLN'
                },
                {
                    title: 'Media miesięczne',
                    helperText: 'prąd, gaz podatek od nieruchomości',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'maintenanceFees',
                    value: calculatorState.maintenanceFees,
                    min: 0,
                    max: 1000,
                    suffix: 'PLN'
                },
                {
                    title: 'Ogłoszenia na portalach',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'portalFees',
                    value: calculatorState.portalFees,
                    min: 0,
                    max: 2000,
                    suffix: 'PLN'
                },
                {
                    title: 'Fotograf',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'photographerAmount',
                    value: calculatorState.photographerAmount,
                    min: 0,
                    max: 1000,
                    suffix: 'PLN'
                },
                {
                    title: 'Pośrednik do sprzedaży',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'saleBrokerPercent',
                    value: calculatorState.saleBrokerPercent,
                    min: 0,
                    max: 100,
                    suffix: '%'
                },
                {
                    title: 'Podatek PCC',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'pcc',
                    value: calculatorState.pcc,
                    min: 0,
                    max: 100,
                    suffix: '%'
                },
                {
                    title: 'Remont do 30 m2',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'renovationFrom1To30Amount',
                    value: calculatorState.renovationFrom1To30Amount,
                    min: 0,
                    max: 2500,
                    suffix: 'PLN'
                },
                {
                    title: 'Remont 30-40 m2',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'renovationFrom30To40Amount',
                    value: calculatorState.renovationFrom30To40Amount,
                    min: 0,
                    max: 2500,
                    suffix: 'PLN'
                },
                {
                    title: 'Remont 40-50 m2',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'renovationFrom40To50Amount',
                    value: calculatorState.renovationFrom40To50Amount,
                    min: 0,
                    max: 2500,
                    suffix: 'PLN'
                },
                {
                    title: 'Remont 50-60 m2',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'renovationFrom50To60Amount',
                    value: calculatorState.renovationFrom50To60Amount,
                    min: 0,
                    max: 2500,
                    suffix: 'PLN'
                },
                {
                    title: 'Remont 60+ m2',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'renovationFrom60PlusAmount',
                    value: calculatorState.renovationFrom60PlusAmount,
                    min: 0,
                    max: 2500,
                    suffix: 'PLN'
                },
                {
                    title: 'Pozycja własna',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'userOption',
                    value: calculatorState.userOption ? calculatorState.userOption.toString() : '0',
                    min: 0,
                    suffix: 'PLN'
                }
            ]
        }
    ];

    return (
        <Wrapper>
            <V2HeaderSection title={'Ustawienia kalkulatora'} handleReset={handleReset}/>
            <Container>
                <CalculatorTable
                    sections={calculatorStructure}
                    calculatorState={calculatorState}
                    onValueChange={handleChange}
                    onUpdateField={handleUpdateField}
                    onResetField={handleResetField}
                />
            </Container>

        </Wrapper>
    );
};

export default CalculatorSettingsPage;
